import { motion } from "framer-motion";
import { fadeUp, staggerContainer } from "../../lib/animations";
import PageHero from "./PageHero";
import GoldButton from "../ui/GoldButton";
import StatCounter from "../ui/StatCounter";

interface StitchFeatureItem {
  title: string;
  desc: string;
}

interface StitchStatItem {
  value: string;
  label: string;
}

interface StitchFeaturePageProps {
  label: string;
  title: string;
  titleAccent?: string;
  subtitle: string;
  features: StitchFeatureItem[];
  ctaLabel: string;
  ctaHref?: string;
  stats?: StitchStatItem[];
  badges?: string[];
  listStyle?: "card" | "tag";
  children?: React.ReactNode;
}

const StitchFeaturePage = ({
  label,
  title,
  titleAccent,
  subtitle,
  features,
  ctaLabel,
  ctaHref = "/contact",
  stats,
  badges,
  listStyle = "card",
  children,
}: StitchFeaturePageProps) => (
  <>
    <PageHero label={label} title={title} titleAccent={titleAccent} subtitle={subtitle} />

    {stats && stats.length > 0 && (
      <section style={{ background: "#0E0E0E", padding: "72px 0" }}>
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 clamp(20px, 4vw, 48px)",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 48,
          }}
        >
          {stats.map((s) => (
            <motion.div key={s.label} variants={fadeUp} style={{ flex: "1 1 180px", textAlign: "center" }}>
              <StatCounter value={s.value} label={s.label} />
            </motion.div>
          ))}
        </motion.div>
      </section>
    )}

    <section style={{ background: "#050505", padding: "0 0 110px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 clamp(20px, 4vw, 48px)" }}>
        {badges && badges.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 40 }}>
            {badges.map((badge) => (
              <span
                key={badge}
                style={{
                  padding: "9px 16px",
                  border: "1px solid rgba(212,168,67,0.25)",
                  borderRadius: 3,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13,
                  color: "#D4A843",
                  background: "rgba(212,168,67,0.06)",
                }}
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          style={{
            display: "grid",
            gridTemplateColumns: listStyle === "card" ? "repeat(auto-fit, minmax(320px, 1fr))" : "repeat(auto-fit, minmax(240px, 1fr))",
            gap: listStyle === "card" ? 24 : 14,
          }}
        >
          {features.map((f) => (
            <motion.div
              key={f.title}
              variants={fadeUp}
              style={
                listStyle === "card"
                  ? {
                      padding: "32px 28px",
                      border: "1px solid rgba(212,168,67,0.18)",
                      borderRadius: 4,
                      background: "#0E0E0E",
                    }
                  : {
                      padding: "18px 20px",
                      border: "1px solid rgba(212,168,67,0.18)",
                      borderRadius: 4,
                      background: "#0E0E0E",
                    }
              }
            >
              <h3
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                  fontSize: listStyle === "card" ? 30 : 24,
                  color: "#F8F4EC",
                  marginBottom: 8,
                }}
              >
                {f.title}
              </h3>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  color: "#8B8378",
                  lineHeight: 1.65,
                  fontWeight: 300,
                }}
              >
                {f.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <div style={{ textAlign: "center", marginTop: 58 }}>
          <GoldButton href={ctaHref}>{ctaLabel}</GoldButton>
        </div>

        {children ? <div style={{ marginTop: 58 }}>{children}</div> : null}
      </div>
    </section>
  </>
);

export default StitchFeaturePage;
